"use client";

import { useEffect, useState } from "react";
import { useLocale } from "@/lib/context/LocaleProvider";
import {
  fetchGenerations,
  type ApiGenerationHistoryItem,
} from "@/lib/api/generations";

const RECENTS_LIMIT = 5;

/**
 * Latest history rows for the sidebar Recents list.
 * Fetches GET /v1/generation when `enabled` (sidebar open + signed in).
 */
export function useSidebarRecents(enabled: boolean) {
  const { locale, hydrated: localeHydrated } = useLocale();

  const [items, setItems] = useState<ApiGenerationHistoryItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled || !localeHydrated) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        // `fetchGenerations` is cached, so reopening the drawer is free; a new
        // run invalidates the namespace and this refetches.
        const { data, meta } = await fetchGenerations({
          page: 1,
          perPage: RECENTS_LIMIT,
          locale,
        });
        if (cancelled) return;
        setItems(data);
        const total = meta.pagination?.total ?? data.length;
        setHasMore(total > RECENTS_LIMIT);
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load sidebar recents", err);
        setItems([]);
        setHasMore(false);
        setLoaded(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, locale, localeHydrated]);

  return { items, hasMore, loading, loaded, limit: RECENTS_LIMIT };
}
